import { useState } from "react";

import { apiClient, type TemporaryCredential } from "../../api/client";
import { PasswordInput } from "../../components/PasswordInput";
import { errorMessage } from "../admin/errors";
import { ConfirmationDialog } from "./ConfirmationDialog";
import type { UserSectionProps } from "./UserDetailDrawer";

type SecurityAction = "password" | "mfa";

export function UserSecuritySection({
  actorRole,
  user,
  onUpdated,
}: UserSectionProps & { actorRole: "manager" | "owner" }) {
  const [temporaryPassword, setTemporaryPassword] = useState("");
  const [passwordStepUp, setPasswordStepUp] = useState("");
  const [mfaStepUp, setMfaStepUp] = useState("");
  const [resetResult, setResetResult] = useState<TemporaryCredential | null>(null);
  const [pending, setPending] = useState<SecurityAction | null>(null);
  const [generating, setGenerating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [copyStatus, setCopyStatus] = useState("");

  async function generatePassword() {
    setGenerating(true);
    setError("");
    setCopyStatus("");
    try {
      setTemporaryPassword(await apiClient.generateTemporaryPassword(user.username));
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setGenerating(false);
    }
  }

  async function confirmAction() {
    if (!pending) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      if (pending === "password") {
        const updated = await apiClient.resetUserPassword(
          user.id,
          temporaryPassword,
          passwordStepUp,
        );
        setResetResult(updated);
        setTemporaryPassword("");
        setPasswordStepUp("");
        setMessage("密码已重置；目标会话已撤销，原 MFA 因子保持不变。");
        onUpdated(updated);
      } else {
        const updated = await apiClient.resetUserMfa(user.id, mfaStepUp);
        setMfaStepUp("");
        setMessage("MFA 已重置；目标会话已撤销，下次登录必须重新注册验证器。");
        onUpdated(updated);
      }
      setPending(null);
    } catch (caught) {
      setPending(null);
      setError(errorMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function copyPassword(password: string) {
    try {
      await navigator.clipboard.writeText(password);
      setCopyStatus("临时密码已复制。关闭详情后将不再显示。");
    } catch {
      setCopyStatus("浏览器无法复制，请在关闭详情前手动保存临时密码。");
    }
  }

  const confirmation = pending ? securityConfirmation(pending, user.username) : null;
  const canResetMfa = actorRole === "owner" && user.role !== "user";

  return (
    <section className="user-detail-section" aria-labelledby="security-section-title">
      <div>
        <span className="eyebrow">安全</span>
        <h3 id="security-section-title">辅助密码重置与 MFA</h3>
      </div>
      <div className="user-security-block">
        <h4>辅助密码重置</h4>
        <p>需要你的 TOTP 或未使用恢复码进行二次认证；重置不会删除目标账户已有的 MFA 因子。</p>
        {resetResult ? (
          <div className="user-generated-password">
            <label htmlFor="reset-result-password">一次性临时密码</label>
            <PasswordInput
              hideLabel="隐藏重置结果临时密码"
              id="reset-result-password"
              readOnly
              showLabel="显示重置结果临时密码"
              value={resetResult.temporary_password}
            />
            <p>有效期至 {new Date(resetResult.temporary_password_expires_at).toLocaleString()}</p>
            <button onClick={() => void copyPassword(resetResult.temporary_password)} type="button">
              复制重置临时密码
            </button>
          </div>
        ) : (
          <div className="user-security-form">
            <button
              disabled={generating || busy}
              onClick={() => void generatePassword()}
              type="button"
            >
              {generating ? "正在生成…" : temporaryPassword ? "重新生成临时密码" : "生成临时密码"}
            </button>
            {temporaryPassword && (
              <>
                <div className="user-generated-password">
                  <label htmlFor="reset-candidate-password">待使用临时密码</label>
                  <PasswordInput
                    hideLabel="隐藏待重置临时密码"
                    id="reset-candidate-password"
                    readOnly
                    showLabel="显示待重置临时密码"
                    value={temporaryPassword}
                  />
                  <button onClick={() => void copyPassword(temporaryPassword)} type="button">
                    复制临时密码
                  </button>
                </div>
                <label>
                  密码重置二次认证码
                  <PasswordInput
                    autoComplete="one-time-code"
                    hideLabel="隐藏密码重置二次认证码"
                    maxLength={32}
                    onChange={(event) => setPasswordStepUp(event.target.value)}
                    showLabel="显示密码重置二次认证码"
                    value={passwordStepUp}
                  />
                </label>
                <button
                  disabled={busy || passwordStepUp.length < 6}
                  onClick={() => setPending("password")}
                  type="button"
                >
                  重置目标密码
                </button>
              </>
            )}
          </div>
        )}
      </div>
      {canResetMfa && (
        <div className="user-security-block">
          <h4>重置特权账户 MFA</h4>
          <p>仅 owner 可执行。目标的验证器、恢复码和活动会话都将失效。</p>
          <label>
            MFA 重置二次认证码
            <PasswordInput
              autoComplete="one-time-code"
              hideLabel="隐藏 MFA 重置二次认证码"
              maxLength={32}
              onChange={(event) => setMfaStepUp(event.target.value)}
              showLabel="显示 MFA 重置二次认证码"
              value={mfaStepUp}
            />
          </label>
          <button
            disabled={busy || mfaStepUp.length < 6}
            onClick={() => setPending("mfa")}
            type="button"
          >
            重置目标 MFA
          </button>
        </div>
      )}
      {message && <p className="status-banner">{message}</p>}
      {copyStatus && <p className="status-banner">{copyStatus}</p>}
      {error && (
        <p className="alert error" role="alert">
          {error}
        </p>
      )}
      {confirmation && (
        <ConfirmationDialog
          busy={busy}
          confirmLabel={confirmation.confirmLabel}
          description={confirmation.description}
          onCancel={() => setPending(null)}
          onConfirm={() => void confirmAction()}
          title={confirmation.title}
        />
      )}
    </section>
  );
}

function securityConfirmation(action: SecurityAction, username: string) {
  if (action === "password") {
    return {
      title: "确认辅助密码重置",
      description: `重置 ${username} 的密码，撤销其全部会话，并要求下次登录替换短期临时密码。已有 MFA 因子不会被删除。`,
      confirmLabel: "确认重置密码",
    };
  }
  return {
    title: "确认重置 MFA",
    description: `使 ${username} 的验证器和恢复码全部失效，撤销其会话，并强制重新注册 MFA。`,
    confirmLabel: "确认重置 MFA",
  };
}
